/* @HEADER
 * @version 1.1.1298 | 2026-10-30
 * @purpose ZVX-DEV-8719 -- says it when the organisation stamp could not place a project: one console entry per project and reason (the recorder keeps the class name, so the reason is in the name) and one telemetry stage with the count by reason.
 * @sidecar projectOrganizationUnassignedReport.ts.header.md
 * @layer lib | @hex _none_ | @ctx project
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-8719

import { getP2pTelemetryClient } from '@modules/realtime/public-api.mjs'
import type { UnassignedProject, UnassignedReason } from './projectOrganizationStamp.ts'

/** Class names only: the recorder's console tap keeps `errorName`, never a message (as for ZVX-DEV-7370). */
class ProjectOrganizationUnassignedIdentityNotLoaded extends Error {
  override name = 'ProjectOrganizationUnassignedIdentityNotLoaded'
}
class ProjectOrganizationUnassignedPersonalWorkspaceUnknown extends Error {
  override name = 'ProjectOrganizationUnassignedPersonalWorkspaceUnknown'
}
class ProjectOrganizationUnassignedNotAMember extends Error {
  override name = 'ProjectOrganizationUnassignedNotAMember'
}

const ERROR_OF_REASON: Record<UnassignedReason, new () => Error> = {
  'identity-not-loaded': ProjectOrganizationUnassignedIdentityNotLoaded,
  'personal-workspace-unknown': ProjectOrganizationUnassignedPersonalWorkspaceUnknown,
  'organization-not-a-member': ProjectOrganizationUnassignedNotAMember,
}

const spoken = new Set<string>()

/** For tests: forget what was already said. */
export function resetUnassignedReportForTests(): void {
  spoken.clear()
}

/**
 * @param unassigned what `stampProjectOrganizations` could not place. Nothing to say for an empty list.
 */
export function reportUnassignedProjects(unassigned: readonly UnassignedProject[]): void {
  if (unassigned.length === 0) return
  const byReason: Partial<Record<UnassignedReason, number>> = {}
  for (const entry of unassigned) {
    byReason[entry.reason] = (byReason[entry.reason] ?? 0) + 1
    const key = `${entry.id}\u0000${entry.reason}`
    if (spoken.has(key)) continue
    spoken.add(key)
    try {
      console.warn(new ERROR_OF_REASON[entry.reason]())
    } catch {
      /* recording must never change what the caller sees */
    }
  }
  try {
    getP2pTelemetryClient().emit(() => ({
      stage: 'project-organization-unassigned',
      fields: { count: unassigned.length, byReason },
    }))
  } catch {
    /* a diagnostics channel that throws must not become a failure */
  }
}
